/**
 * Dashboard 大屏可视化项目主入口文件
 * 配置了 Vue 3、ECharts、autofit.js 等核心依赖
 * Created: 2025年10月20日
 */

// 导入全局样式文件
import './assets/main.css' // 基础样式，配合 autofit.js
import './assets/dashboard.css' // 大屏可视化专用样式

// 导入 autofit.js 实现屏幕自适应等比缩放
import autofit from 'autofit.js'

// Vue 3 核心依赖
import { createApp } from 'vue'
import { createPinia } from 'pinia'

// 应用组件和路由
import App from './App.vue'
import router from './router'

// 全局组件
import Loading from './components/Loading.vue'

// ===== ECharts 图表库配置 =====
// 按需引入 ECharts 核心模块和 Vue-ECharts 组件
import { use } from 'echarts/core'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers' // Canvas 渲染器
// 引入所有图表类型 - 支持大屏可视化各种数据展示需求
import {
  BarChart, // 柱状图
  LineChart, // 折线图
  PieChart, // 饼图
  ScatterChart, // 散点图
  RadarChart, // 雷达图
  MapChart, // 地图
  TreeChart, // 树图
  TreemapChart, // 矩形树图
  GraphChart, // 关系图
  GaugeChart, // 仪表盘
  FunnelChart, // 漏斗图
  ParallelChart, // 平行坐标
  SankeyChart, // 桑基图
  BoxplotChart, // 箱线图
  CandlestickChart, // K线图
  EffectScatterChart, // 带涟漪效果的散点图
  LinesChart, // 线图
  HeatmapChart, // 热力图
  PictorialBarChart, // 象形柱图
  ThemeRiverChart, // 主题河流图
  SunburstChart, // 旭日图
  CustomChart, // 自定义图表
} from 'echarts/charts'
// 引入 ECharts 组件 - 提供图表交互和辅助功能
import {
  GridComponent, // 直角坐标系网格
  PolarComponent, // 极坐标系
  GeoComponent, // 地理坐标系
  SingleAxisComponent, // 单轴
  ParallelComponent, // 平行坐标系
  CalendarComponent, // 日历坐标系
  GraphicComponent, // 图形元素
  ToolboxComponent, // 工具栏
  TooltipComponent, // 提示框
  AxisPointerComponent, // 坐标轴指示器
  BrushComponent, // 区域选择
  TitleComponent, // 标题
  TimelineComponent, // 时间轴
  MarkPointComponent, // 标注点
  MarkLineComponent, // 标注线
  MarkAreaComponent, // 标注区域
  LegendComponent, // 图例
  DataZoomComponent, // 数据缩放
  DataZoomInsideComponent, // 内置数据缩放
  DataZoomSliderComponent, // 滑块数据缩放
  VisualMapComponent, // 视觉映射
  VisualMapContinuousComponent, // 连续型视觉映射
  VisualMapPiecewiseComponent, // 分段型视觉映射
  AriaComponent, // 无障碍访问
  TransformComponent, // 数据变换
} from 'echarts/components'
// Vue-ECharts 组件和主题系统
import VChart, { THEME_KEY } from 'vue-echarts'

// ===== ECharts 组件注册 =====
// 将所有图表类型和组件注册到 ECharts 核心中
use([
  // 渲染器
  CanvasRenderer,

  // 图表类型
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  RadarChart,
  MapChart,
  TreeChart,
  TreemapChart,
  GraphChart,
  GaugeChart,
  FunnelChart,
  ParallelChart,
  SankeyChart,
  BoxplotChart,
  CandlestickChart,
  EffectScatterChart,
  LinesChart,
  HeatmapChart,
  PictorialBarChart,
  ThemeRiverChart,
  SunburstChart,
  CustomChart,

  // 组件系统
  GridComponent,
  PolarComponent,
  GeoComponent,
  SingleAxisComponent,
  ParallelComponent,
  CalendarComponent,
  GraphicComponent,
  ToolboxComponent,
  TooltipComponent,
  AxisPointerComponent,
  BrushComponent,
  TitleComponent,
  TimelineComponent,
  MarkPointComponent,
  MarkLineComponent,
  MarkAreaComponent,
  LegendComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  DataZoomSliderComponent,
  VisualMapComponent,
  VisualMapContinuousComponent,
  VisualMapPiecewiseComponent,
  AriaComponent,
  TransformComponent,
])

// ===== autofit.js 自适应配置 =====
// 初始化 autofit.js，实现大屏等比例缩放适配
// 设计稿尺寸：1920x1080，目标元素：#app
autofit.init({
  designHeight: 1080, // 设计稿高度
  designWidth: 1920, // 设计稿宽度
  renderDom: '#app', // autofit 作用的 DOM 元素
  resize: true, // 监听窗口 resize 事件
})

// 引入 v-viewer 组件
import Viewer from 'v-viewer'
import 'viewerjs/dist/viewer.css'

// ===== Vue 应用实例创建 =====
const app = createApp(App)

// ===== 全局错误处理 =====
app.config.errorHandler = (err, instance, info) => {
  console.error('全局错误捕获:', err)
  console.error('错误信息:', info)
  console.error('组件实例:', instance)
  // 可以在这里上报错误到监控平台
}

// Vue 警告处理
app.config.warnHandler = (msg, instance, trace) => {
  console.warn('Vue 警告:', msg)
  console.warn('组件追踪:', trace)
}

// ===== 全局组件和插件配置 =====
// 全局注册 Vue-ECharts 组件，在任何组件中都可以使用 <VChart>
app.component('VChart', VChart)

// 全局注册 Loading 组件
app.component('Loading', Loading)

// 提供 ECharts 暗色主题，适合大屏可视化的深色背景
app.provide(THEME_KEY, 'dark')

// 全局注册 v-viewer 组件
app.use(Viewer)
Viewer.setDefaults({
  // title: false,
  toolbar: false,
  // navbar: false,
  button: false
})

// 注册状态管理 Pinia
app.use(createPinia())

// 注册 Vue Router 路由系统
app.use(router)

// ===== 全局 ECharts 自适应处理 =====
// 挂载 ECharts 到全局对象，供 resize 处理使用
window.echarts = echarts

// 监听窗口大小变化，确保所有 ECharts 实例正确重绘
let resizeTimer = null
// window.addEventListener('resize', () => {
//   // 防抖处理，避免频繁触发
//   if (resizeTimer) clearTimeout(resizeTimer)
//   resizeTimer = setTimeout(() => {
//     // 使用 Set 避免重复 resize 同一个图表实例
//     const resizedInstances = new Set()

//     // 方案1: 通过 .chart class 获取 VChart 组件容器
//     const chartContainers = document.querySelectorAll('.chart')
//     chartContainers.forEach((container) => {
//       const chartInstance = echarts.getInstanceByDom(container)
//       if (chartInstance && !chartInstance.isDisposed() && !resizedInstances.has(chartInstance)) {
//         console.log(chartInstance.resize(),'chartInstance1111');
        
//         chartInstance.resize()
//         resizedInstances.add(chartInstance)
//       }
//     })

//     // 调试信息 (生产环境可删除)
//     if (import.meta.env.DEV && resizedInstances.size > 0) {
//       console.log(`📊 Resized ${resizedInstances.size} chart instance(s)`)
//     }
//   }, 250) // 增加延迟到 250ms，确保 autofit.js 完成缩放后再 resize
// })

// ===== 应用挂载 =====
// 将 Vue 应用挂载到 #app 元素上
// 注意：autofit.js 也会作用于 #app 元素，实现自动缩放
app.mount('#app')
